'use client';

type RecaptchaApi = {
  ready: (cb: () => void) => void;
  execute: (widgetId: number) => Promise<string>;
  render: (
    container: HTMLElement | string,
    params: {
      sitekey: string;
      size?: 'invisible' | 'compact' | 'normal';
      badge?: 'inline' | 'bottomright' | 'bottomleft';
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ) => number;
  reset: (widgetId?: number) => void;
};

const getRecaptcha = (): RecaptchaApi | undefined =>
  (window as Window & { grecaptcha?: RecaptchaApi }).grecaptcha;

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

let recaptchaLoader: Promise<void> | null = null;
let widgetId: number | null = null;
let containerEl: HTMLElement | null = null;
let pendingTokenResolver: ((token: string | null) => void) | null = null;

const handleToken = (token: string | null) => {
  if (pendingTokenResolver) {
    pendingTokenResolver(token);
    pendingTokenResolver = null;
  }
};

const loadRecaptchaScript = () => {
  if (recaptchaLoader) return recaptchaLoader;

  recaptchaLoader = new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined' || !SITE_KEY) {
      resolve();
      return;
    }

    if (getRecaptcha()?.ready) {
      getRecaptcha()?.ready(() => resolve());
      return;
    }

    const existingScript = document.querySelector(
      'script[src^="https://www.google.com/recaptcha/api.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google reCAPTCHA')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (getRecaptcha()?.ready) {
        getRecaptcha()?.ready(() => resolve());
      } else {
        resolve();
      }
    };
    script.onerror = () => reject(new Error('Failed to load Google reCAPTCHA'));
    document.head.appendChild(script);
  });

  return recaptchaLoader;
};

const ensureWidget = async () => {
  if (!SITE_KEY || typeof window === 'undefined') return null;

  await loadRecaptchaScript();

  if (widgetId !== null) return widgetId;

  containerEl = document.createElement('div');
  containerEl.id = 'recaptcha-v2-invisible-container';
  containerEl.style.display = 'none';
  document.body.appendChild(containerEl);

  widgetId =
    getRecaptcha()?.render(containerEl, {
      sitekey: SITE_KEY,
      size: 'invisible',
      badge: 'bottomright',
      callback: (token: string) => handleToken(token),
      'expired-callback': () => handleToken(null),
      'error-callback': () => handleToken(null),
    }) ?? null;

  return widgetId;
};

export const isRecaptchaConfigured = () =>
  typeof window !== 'undefined' && Boolean(SITE_KEY);

export const getRecaptchaToken = async () => {
  if (!SITE_KEY || typeof window === 'undefined') return null;

  try {
    const id = await ensureWidget();
    if (id === null || !getRecaptcha()?.execute) return null;

    return await new Promise<string | null>((resolve) => {
      pendingTokenResolver = (token) => {
        if (id !== null) {
          getRecaptcha()?.reset(id);
        }
        resolve(token);
      };

      try {
        getRecaptcha()?.execute(id);
      } catch (err) {
        console.error('Failed to execute reCAPTCHA', err);
        pendingTokenResolver = null;
        resolve(null);
      }

      // Failsafe timeout
      setTimeout(() => {
        if (pendingTokenResolver) {
          pendingTokenResolver = null;
          resolve(null);
        }
      }, 8000);
    });
  } catch (error) {
    console.error('Failed to resolve reCAPTCHA token', error);
    return null;
  }
};
